import React, { useEffect } from "react";
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

const Pagination = ({ totalPages, currentPage, onPageChange, className = '' }) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const handleClick = (e, page) => {
    e.preventDefault();
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const ellipses = (
      <span
        key="ellipses"
        className="px-4 py-2 text-sm font-semibold text-gray-700"
      >
        ...
      </span>
    );

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(
          <a
            key={i}
            href="#"
            onClick={(e) => handleClick(e, i)}
            className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold border-r border-gray-200 ${
              i === currentPage ? "bg-indigo-600 text-white" : "text-gray-900"
            } focus:z-20 focus:outline-offset-0`}
          >
            {i}
          </a>
        );
      }
    } else {
      const start = currentPage > 3 ? currentPage - 2 : 1;
      const end = currentPage < totalPages - 2 ? currentPage + 2 : totalPages;

      if (start > 1) {
        pageNumbers.push(
          <a
            key={1}
            href="#"
            onClick={(e) => handleClick(e, 1)}
            className="relative inline-flex items-center px-4 py-2 text-sm font-semibold border-r border-gray-200 text-custom-dark-grey focus:z-20 focus:outline-offset-0"
          >
            1
          </a>
        );
        if (start > 2) pageNumbers.push(ellipses);
      }

      for (let i = start; i <= end; i++) {
        pageNumbers.push(
          <a
            key={i}
            href="#"
            onClick={(e) => handleClick(e, i)}
            className={`relative inline-flex items-center px-4 py-2 text-sm font-normal border-r border-gray-200 ${
              i === currentPage
                ? "bg-indigo-600 text-white"
                : "text-custom-dark-grey"
            } focus:z-20 focus:outline-offset-0`}
          >
            {i}
          </a>
        );
      }

      if (end < totalPages) {
        if (end < totalPages - 1) pageNumbers.push(ellipses);
        pageNumbers.push(
          <a
            key={totalPages}
            href="#"
            onClick={(e) => handleClick(e, totalPages)}
            className="relative border-x border-gray-200 inline-flex items-center px-4 py-2 text-sm font-normal text-custom-dark-grey focus:z-20 focus:outline-offset-0"
          >
            {totalPages}
          </a>
        );
      }
    }

    return pageNumbers;
  };

  return (
    <div className={`flex items-center justify-between bg-white px-4 py-3 sm:px-6 ${className}`}>
      <div className="flex flex-1 justify-between sm:hidden">
        {/* Mobile version - you can keep or remove as needed */}
      </div>
      <div className=" sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div className="hidden sm:flex">
          <p className="text-sm text-gray-700">
            Showing {currentPage} of {totalPages} pages
          </p>
        </div>
        <div>
          <nav
            className="isolate inline-flex rounded-md shadow-sm border border-gray-300"
            aria-label="Pagination"
          >
            <a
              href="#"
              onClick={(e) => handleClick(e, currentPage - 1)}
              className={`relative inline-flex items-center px-2 py-2 text-gray-400 border-r border-gray-200 ${
                currentPage === 1 ? "hidden" : ""
              }`}
            >
              <span className="sr-only">Previous</span>
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
            </a>
            {renderPageNumbers()}
            <a
              href="#"
              onClick={(e) => handleClick(e, currentPage + 1)}
              className={`relative inline-flex items-center px-2 py-2 text-gray-400 border-r border-gray-200  ${
                currentPage === totalPages ? "hidden" : ""
              }`}
            >
              <span className="sr-only">Next</span>
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
            </a>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
